/**
 * 订单表单弹窗「时间安排」区块：预约/结束日期、全天开关、自定义时段、预计到达时间
 */
import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import type { OrderFormData } from '../../OrderFormSchema';
import { SectionHeader } from '../SectionHeader';
import { inp, lbl } from '../utils';

interface TimeSectionProps {
  control: Control<OrderFormData>;
  errors: FieldErrors<OrderFormData>;
  form: OrderFormData;
  orderStartMin: string | undefined;
  durationHint: string;
  arrivalMaxDateTime: string;
  /** 编辑态：预约日期提交后不可更改，只显示不输入（服务端同一道闸门，改 400） */
  dateLocked: boolean;
  handleEndDateChange: (val: string) => void;
  handleEndTimeChange: (val: string) => void;
  handleArrivalDateTimeChange: (val: string) => void;
}

export function TimeSection({ control, errors, form, orderStartMin, durationHint, arrivalMaxDateTime, dateLocked, handleEndDateChange, handleEndTimeChange, handleArrivalDateTimeChange }: TimeSectionProps) {
  return (
    /* ② 时间安排 */
    <div data-section="time">
      <SectionHeader icon="ri-calendar-event-line" title="时间安排" color="bg-indigo-50 text-indigo-500" />
      <div className="space-y-4">
        {/* 日期行 */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={lbl}>预约日期 <span className="text-rose-400">*</span></label>
            {dateLocked ? (
              <>
                {/* 与「结清凭证」只读框同款：值还在表单里（提交时原样回传，服务端同值放行），只是不给改 */}
                <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-calendar-line text-gray-400 text-sm" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{form.orderStartDate || '—'}</span>
                  <i className="ri-lock-line text-gray-400 text-xs ml-auto" />
                </div>
                <p className="text-xs text-gray-400 mt-1">提交后不可更改；如需改期请取消这张单后重新提交</p>
              </>
            ) : (
              <>
                <Controller
                  name="orderStartDate"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="date"
                      {...field}
                      min={orderStartMin}
                      className={inp(errors.orderStartDate?.message)}
                    />
                  )}
                />
                {errors.orderStartDate && <p className="text-xs text-rose-500 mt-1">{errors.orderStartDate.message}</p>}
              </>
            )}
          </div>
          {form.orderStartDate && (
            <div>
              <label className={lbl}>
                结束日期
                <span className="ml-1.5 text-xs font-normal text-gray-400 inline-flex items-center gap-0.5">
                  <i className="ri-information-line" />
                  单日不需要填写
                </span>
              </label>
              <input
                type="date"
                value={form.orderEndDate}
                min={form.orderStartDate || undefined}
                onChange={e => handleEndDateChange(e.target.value)}
                className={inp(errors.orderEndDate?.message)}
              />
              {errors.orderEndDate && <p className="text-xs text-rose-500 mt-1">{errors.orderEndDate.message}</p>}
            </div>
          )}
        </div>

        {/* 全天 toggle + 时段 — 仅在填写预约日期后显示 */}
        {form.orderStartDate && (
          <>
            {/* 全天 toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-sun-line text-amber-400 text-sm" />
                </div>
                <span className="text-sm text-gray-600">全天服务</span>
                <span className="text-xs text-gray-400">（关闭后可设定具体时段）</span>
              </div>
              <Controller
                name="isFullDay"
                control={control}
                render={({ field }) => (
                  <button
                    onClick={() => field.onChange(!field.value)}
                    className={`w-10 h-6 rounded-full transition-colors relative cursor-pointer shrink-0 ${field.value ? 'bg-teal-500' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${field.value ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                )}
              />
            </div>

            {/* 自定义时间段 */}
            {!form.isFullDay && (
              <div className="grid grid-cols-2 gap-3 p-3 bg-indigo-50/60 rounded-lg border border-indigo-100">
                <div>
                  <label className={lbl}>开始时间</label>
                  <Controller
                    name="orderTimeStart"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="time"
                        {...field}
                        className={inp()}
                      />
                    )}
                  />
                </div>
                <div>
                  <label className={lbl}>结束时间</label>
                  <input
                    type="time"
                    value={form.orderTimeEnd}
                    onChange={e => handleEndTimeChange(e.target.value)}
                    className={inp(errors.orderTimeEnd?.message)}
                  />
                  {errors.orderTimeEnd && <p className="text-xs text-rose-500 mt-1">{errors.orderTimeEnd.message}</p>}
                </div>
              </div>
            )}

            {/* 服务时长提示 */}
            {durationHint && (
              <div className="flex items-center gap-2.5 p-3 bg-teal-50 border border-teal-100 rounded-lg">
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-time-line text-teal-500 text-sm" />
                </div>
                <span className="text-xs text-gray-500">服务时长</span>
                <span className="text-sm font-semibold text-teal-600">{durationHint}</span>
              </div>
            )}
          </>
        )}

        {/* 预计到达时间 — 仅在预约日期填写后显示 */}
        {form.orderStartDate && (
          <div>
            <label className={lbl}>
              预计到达时间
              <span className="ml-1.5 text-xs font-normal text-gray-400 inline-flex items-center gap-0.5">
                <i className="ri-information-line" />
                仅登记到达时间，不参与档期与金额判定
              </span>
            </label>
            <input
              type="datetime-local"
              value={form.arrivalDateTime}
              max={arrivalMaxDateTime || undefined}
              onChange={e => handleArrivalDateTimeChange(e.target.value)}
              className={inp(errors.arrivalDateTime?.message)}
            />
            {errors.arrivalDateTime && <p className="text-xs text-rose-500 mt-1">{errors.arrivalDateTime.message}</p>}
            {/* 提示当前限制规则 */}
            {!errors.arrivalDateTime && arrivalMaxDateTime && (
              <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                  <i className="ri-time-line" />
                </div>
                最晚可填 {arrivalMaxDateTime.replace('T', ' ')}
                {!form.isFullDay && form.orderTimeStart && <span className="text-amber-500 ml-1">（开始时间前30分钟）</span>}
                {form.isFullDay && <span className="ml-1">（全天服务上限）</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}